import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Drawer,
  InsightIcon,
  SkeletonLoader,
} from '@toggle/design-system';
import { useDisableScroll } from '@toggle/helpers';
import { formatTicker, InsightDirection } from '@toggle/toggle';
import { isSameDay } from 'date-fns';
import React, { ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { DrawerContentWrapper } from '~/components/drawer-content-wrapper/DrawerContentWrapper';
import { ErrorMessageWrapper } from '~/components/error-message-wrapper/ErrorMessageWrapper';
import {
  ExpandableDrawer,
  EXPANDED_VALUE,
} from '~/components/expandable-drawer/ExpandableDrawer';
import { useInsightDrawer } from '~/hooks/use-insight-drawer/useInsightDrawer';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { createEntityQueryOptions } from '~/react-query-hooks/entities/useEntities.utils';
import { queryKeys } from '~/routes/query-params';
import { useGetInsightWidgetData } from '~/services/insights/insights-service';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { useFlags } from '~/stores/use-flags/useFlags';
import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { getExploreDirection } from '~/views/dashboard/whats-new-widget/components/insight-card/utils';
import { getConditionIcons } from '~/views/insights/components/insight-icons/utils';
import { InsightIconsRoot } from '~/views/insights/components/insight-icons-root/InsightIconsRoot';
import { DownloadButton } from '~/views/stream/components/download-button/DownloadButton';
import { ScheduleDemoBanner } from '~/views/stream/components/schedule-demo-banner/ScheduleDemoBanner';
import { handleDownloadDrawer } from '~/views/stream/utils/handleDownloadDrawer';
import { EntityFullViewDrawer } from '~/widgets/catalyst-insights-drawer/catalyst-drawer-header/company-catalyst-drawer/full-view-drawer/EntityFullViewDrawer';
import { EntityHeaderWatchlist } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/entity-header-watchlist/EntityHeaderWatchlist';

import { InsightDrawerCategoryRow } from './components/insights-drawer-content/InsightDrawerCategoryRow';
import { InsightsDrawerContent } from './components/insights-drawer-content/InsightsDrawerContent';
import { RevampInsightsDrawerContent } from './components/insights-drawer-content/RevampInsightsDrawerContent';
import styles from './InsightsDrawer.module.scss';

const INSIGHTS_DRAWER_WIDTH = 700;

export type InsightsDrawerProps = {
  insightId: string;
};

export const InsightsDrawer = ({ insightId }: InsightsDrawerProps) => {
  useDisableScroll();
  const { t } = useTranslation([
    'widget',
    'common',
    'stream',
    'scenarioCommander',
    'analyze',
    'scenario',
    'screener',
    'insights',
    'chart',
  ]);

  const { closeDrawer } = useInsightDrawer();
  const { data, isLoading } = useGetInsightWidgetData(insightId);
  const activeEntityTag = data?.data?.entity?.tag;
  const isUniversalDrawerEnabled = useFlags(state =>
    state.getFeatureFlagState('universal_drawer')
  );
  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );
  const useUniversalDrawer = isUniversalDrawerEnabled && !isBasicOrLightUser;
  const { params } = useQueryParams();
  const isExpanded = params[queryKeys.drawerExpanded] === EXPANDED_VALUE;

  /*
   * Separate entity query when tag is unchanged,
   * so we can keep the expanded chart/subheader stable
   * between insights for the same entity
   */
  const {
    data: entityQueryData,
    isLoading: isEntityLoading,
    isFetching: isEntityFetching,
    isPending: isEntityPending,
  } = useQuery({
    ...createEntityQueryOptions(activeEntityTag ?? ''),
    placeholderData: previousData =>
      previousData?.data?.tag === activeEntityTag ? previousData : undefined,
  });
  const entityFromEntityQuery = entityQueryData?.data;

  const navigateToAssetOverviewLabel = isUniversalDrawerEnabled
    ? t('widget:themeOverview.drawer.viewOnAssetOverview')
    : t('widget:assetOverview.assetOverview');

  const contentWrapperRef = useRef<HTMLDivElement>(null);

  // Temp: update to navigate from watchlist when available
  const { navigateToPage } = useNavigateToAssetOverview();

  const navigateToAssetOverview = () => {
    if (activeEntityTag) {
      navigateToPage({ entityTag: activeEntityTag });
    }
  };

  const getDrawerContent = () => {
    if (isLoading) {
      return (
        <SkeletonLoader
          className={styles.loadingSkeletonWrapper}
          areas={[
            {
              styles: {
                width: '100%',
                height: '64px',
                borderRadius: '8px',
              },
            },
            {
              styles: {
                width: '100%',
                height: '136px',
                borderRadius: '8px',
              },
            },
            {
              styles: {
                width: '100%',
                height: '400px',
                borderRadius: '8px',
              },
            },
            {
              styles: {
                width: '100%',
                height: '400px',
                borderRadius: '8px',
              },
            },
          ]}
        />
      );
    }

    if (data?.error || !data?.data) {
      return (
        <ErrorMessageWrapper variant="empty" className={styles.errorWrapper} />
      );
    }

    let icon: ReactNode = null;
    const { insight } = data.data;

    const conditions = getConditionIcons({
      conditions: insight.metadata.conditions,
      kind: insight.metadata.kind,
    });

    if (conditions.length > 0) {
      const isCombined = conditions.length > 1;
      const isNewInsight = isSameDay(new Date(insight.created_at), new Date());
      icon = (
        <InsightIconsRoot
          conditions={conditions}
          direction={getExploreDirection(insight.metadata.direction)}
          isStarred={isNewInsight}
          isCombined={isCombined}
        />
      );
    } else {
      const isBullish =
        data.data.insight.metadata.direction === InsightDirection.Bullish;
      const showSparkles = isSameDay(
        new Date(
          data.data.insight.metadata?.last_value_date ??
            data.data.insight.created_at
        ),
        new Date()
      );
      icon = (
        <InsightIcon
          isBullish={isBullish}
          showSparkles={showSparkles}
          options={{ iconSize: 32 }}
        />
      );
    }

    return (
      <div ref={contentWrapperRef}>
        {useUniversalDrawer ? (
          <RevampInsightsDrawerContent
            insightsData={data.data}
            header={() => (
              <div className={styles.wrapper}>
                <InsightDrawerCategoryRow insight={insight} />

                <div className={styles.title}>
                  {data.data.insight?.simple_title ??
                    data.data.insight.card.name}
                </div>
              </div>
            )}
            showViewMore
            showEntityHeader={!isExpanded}
          />
        ) : (
          <InsightsDrawerContent
            insightsData={data.data}
            header={() => (
              <div className={styles.header}>
                {icon}
                <div className={styles.title}>
                  {data.data.insight?.simple_title ??
                    data.data.insight.card.name}
                </div>
              </div>
            )}
            showViewMore
          />
        )}
        <ScheduleDemoBanner />
      </div>
    );
  };

  const handleDownload = () => {
    if (contentWrapperRef.current) {
      handleDownloadDrawer({
        contentToPrint: contentWrapperRef.current,
        entityTag: data?.data?.entity.tag ?? null,
      });
    }
  };

  return (
    <>
      {useUniversalDrawer ? (
        <ExpandableDrawer
          className={styles.insightsDrawer}
          data-testid="insights-drawer"
          defaultWidth={INSIGHTS_DRAWER_WIDTH}
          isOpen
          handleCloseClick={closeDrawer}
          label={t('insights:viewAllSection.whatElseIsMoving', {
            name: entityFromEntityQuery
              ? formatTicker(entityFromEntityQuery)
              : '',
          })}
          headerButton={
            <div className={styles.headerButtons}>
              <Button
                iconName="Expand"
                label={navigateToAssetOverviewLabel}
                onClick={navigateToAssetOverview}
                variant="empty"
                size="xsmall"
              />
              <DownloadButton onClick={handleDownload} />
            </div>
          }
          fullViewContent={
            <EntityFullViewDrawer
              trackingSource={POSTHOG_TRACKING_SOURCES.SCENARIO_INSIGHT_DRAWER}
              entity={entityFromEntityQuery}
              isEntityLoading={isEntityLoading}
              isEntityPending={isEntityPending}
            />
          }
          subHeaderNode={
            !!entityFromEntityQuery ? (
              <EntityHeaderWatchlist
                entity={entityFromEntityQuery}
                logoSize={48}
                className={styles.entityHeaderWatchlist}
                isFetching={isEntityFetching}
              />
            ) : (
              <SkeletonLoader
                areas={[
                  {
                    styles: {
                      width: '100%',
                      height: '48px',
                      borderRadius: '8px',
                    },
                  },
                ]}
              />
            )
          }
        >
          <DrawerContentWrapper>{getDrawerContent()}</DrawerContentWrapper>
        </ExpandableDrawer>
      ) : (
        <Drawer
          data-testid="insights-drawer"
          isOpen
          className={styles.insightsDrawer}
          handleCloseClick={closeDrawer}
          headerButton={
            <div className={styles.headerButtons}>
              <Button
                iconName="Expand"
                label={navigateToAssetOverviewLabel}
                onClick={navigateToAssetOverview}
                variant="empty"
                size="xsmall"
              />
              <DownloadButton onClick={handleDownload} />
            </div>
          }
        >
          <DrawerContentWrapper>{getDrawerContent()}</DrawerContentWrapper>
        </Drawer>
      )}
    </>
  );
};
